/**
 * Conservative GST presentation for business expense (tax-time) reports.
 * Bank feed lines are not tax invoices; do not imply claimable GST from 1/11 estimates.
 */

import {
  buildGstSummary,
  type GstCategoryLike,
  type GstTransactionLike,
} from '../../lib/regions/au/reporting/report-gst-summary';
import type { GstSummary, ReportTransactionRow } from '../../types/report.types';

export function buildBusinessExpenseGstSummaryFromRows(
  rows: ReportTransactionRow[]
): GstSummary | null {
  if (!rows.length) return null;
  const totalSpend = rows.reduce(
    (s, r) => s + (r.expenseAmountAud ?? Math.abs(r.amount)),
    0
  );
  const confirmed = rows.filter((r) => r.gstStatus === 'includes_gst');
  const gstIncludedConfirmed = confirmed.reduce((s, r) => {
    const aud = r.expenseAmountAud ?? Math.abs(r.amount);
    return s + aud / 11;
  }, 0);
  const inclusiveRef = rows
    .filter((r) => r.gstStatus === 'includes_gst')
    .reduce((s, r) => s + (r.expenseAmountAud ?? Math.abs(r.amount)), 0);
  const exclusiveRef = rows
    .filter((r) => r.gstStatus === 'no_gst')
    .reduce((s, r) => s + (r.expenseAmountAud ?? Math.abs(r.amount)), 0);
  const unverified = rows
    .filter((r) => r.gstStatus === 'unknown' || r.gstStatus == null)
    .reduce((s, r) => s + (r.expenseAmountAud ?? Math.abs(r.amount)), 0);

  return {
    inclusiveTotal: inclusiveRef,
    exclusiveTotal: exclusiveRef,
    netGst: 0,
    gstIncludedConfirmed,
    gstUnverifiedSpend: unverified || totalSpend,
  };
}

export function buildBusinessExpenseGstSummary(
  transactions: GstTransactionLike[],
  categories: GstCategoryLike[]
): GstSummary | null {
  const base = buildGstSummary(transactions, categories);
  if (!base) return null;

  const totalSpend = base.inclusiveTotal + base.exclusiveTotal;

  return {
    inclusiveTotal: base.inclusiveTotal,
    exclusiveTotal: base.exclusiveTotal,
    netGst: 0,
    gstIncludedConfirmed: 0,
    gstUnverifiedSpend: totalSpend,
  };
}

export const BUSINESS_EXPENSE_GST_DISCLAIMER =
  'Bank and card descriptions are not tax invoices. GST credits can only be claimed with valid tax invoices and correct supplier GST status. Overseas digital services often have no Australian GST.';
